import type { AppNotification, AuthUser, Game, GuildDetailPayload, GuildSummary, MeSummary, PlayerProfile, SessionDetailPayload, SessionSummary } from './types';

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers }
  });
  if (!response.ok) {
    let message = `Request failed (${response.status}).`;
    try {
      const payload = await response.json() as { error?: string };
      if (payload.error) message = payload.error;
    } catch {
      // Keep the generic message when the response is not JSON.
    }
    throw new Error(message);
  }
  return await response.json() as T;
}

export const NOTIFICATION_CATEGORIES = ['sessionReminders', 'reviewReminders', 'sessionRoster', 'sessionUpdates', 'guildActivity'] as const;
export type NotifyCategory = (typeof NOTIFICATION_CATEGORIES)[number];
export type NotifyPrefs = Record<NotifyCategory, boolean>;

export const NOTIFY_CATEGORY_META: Record<NotifyCategory, { label: string; hint: string }> = {
  sessionReminders: { label: 'Session reminders', hint: 'Before a session you have joined starts (30 minutes out and at go time).' },
  reviewReminders: { label: 'Review reminders', hint: 'After sessions end, while the review window is open.' },
  sessionRoster: { label: 'Players joining or leaving', hint: 'When someone joins or leaves a session you host.' },
  sessionUpdates: { label: 'Session updates', hint: 'Edits, cancellations, waitlist spots, and new recurring sessions.' },
  guildActivity: { label: 'Guild activity', hint: 'Announcements, join requests, and role changes in your guilds.' }
};

export interface AccountSecurity {
  emailVerified: boolean;
  hasPassword: boolean;
  isAdmin: boolean;
  notifyPrefs: NotifyPrefs;
}

export interface BlockedPlayer {
  userId: string;
  username: string;
  displayName: string;
  createdAt: string;
}

export interface AdminReport {
  id: string;
  reason: string;
  details: string | null;
  status: string;
  created_at: string;
  session_id: string | null;
  reporter_username: string;
  subject_username: string;
  subject_id: string;
}

export interface ProviderAvailability {
  discord: boolean;
  google: boolean;
  emailSending: boolean;
}

export interface AuthState {
  authenticated: boolean;
  user: AuthUser | null;
  profile: PlayerProfile | null;
  account: AccountSecurity | null;
  providers: ProviderAvailability;
}

export const api = {
  authState: () => apiFetch<AuthState & { ok: boolean }>('/api/auth/me'),
  signup: (email: string, username: string, password: string) =>
    apiFetch<{ user: AuthUser }>('/api/auth/signup', { method: 'POST', body: JSON.stringify({ email, username, password }) }),
  login: (email: string, password: string) =>
    apiFetch<{ user: AuthUser }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => apiFetch('/api/auth/logout', { method: 'POST', body: '{}' }),
  forgotPassword: (email: string) =>
    apiFetch<{ message: string; emailConfigured: boolean; devLink?: string }>('/api/auth/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token: string, password: string) =>
    apiFetch<{ user: AuthUser }>('/api/auth/reset', { method: 'POST', body: JSON.stringify({ token, password }) }),
  requestEmailVerification: () =>
    apiFetch<{ verified: boolean; message: string; emailConfigured: boolean; devLink?: string }>('/api/auth/verify-email', { method: 'POST', body: '{}' }),
  confirmEmailVerification: (token: string) =>
    apiFetch<{ verified: boolean }>('/api/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) }),
  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch<{ hadPassword: boolean }>('/api/auth/password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  unlinkAccount: (provider: string) =>
    apiFetch(`/api/linked-accounts/${encodeURIComponent(provider)}`, { method: 'DELETE' }),

  guilds: () => apiFetch<{ guilds: GuildSummary[] }>('/api/guilds'),
  createGuild: (fields: { name: string; tag: string; description: string; joinPolicy: string }) =>
    apiFetch<{ slug: string }>('/api/guilds', { method: 'POST', body: JSON.stringify(fields) }),
  guild: (slug: string) => apiFetch<GuildDetailPayload>(`/api/guilds/${encodeURIComponent(slug)}`),
  updateGuild: (slug: string, fields: Record<string, unknown>) =>
    apiFetch(`/api/guilds/${encodeURIComponent(slug)}`, { method: 'PATCH', body: JSON.stringify(fields) }),
  joinGuild: (slug: string) =>
    apiFetch<{ status: 'active' | 'pending' }>(`/api/guilds/${encodeURIComponent(slug)}/membership`, { method: 'POST', body: '{}' }),
  leaveGuild: (slug: string) =>
    apiFetch<{ disbanded?: boolean }>(`/api/guilds/${encodeURIComponent(slug)}/membership`, { method: 'DELETE' }),
  guildPending: (slug: string) =>
    apiFetch<{ pending: Array<{ userId: string; username: string; requestedAt: string }> }>(`/api/guilds/${encodeURIComponent(slug)}/members`),
  guildMemberAction: (slug: string, userId: string, action: string) =>
    apiFetch(`/api/guilds/${encodeURIComponent(slug)}/members`, { method: 'PATCH', body: JSON.stringify({ userId, action }) }),
  postGuildAnnouncement: (slug: string, body: string) =>
    apiFetch(`/api/guilds/${encodeURIComponent(slug)}/posts`, { method: 'POST', body: JSON.stringify({ body }) }),
  deleteGuildPost: (slug: string, postId: string) =>
    apiFetch(`/api/guilds/${encodeURIComponent(slug)}/posts`, { method: 'DELETE', body: JSON.stringify({ postId }) }),

  kickPlayer: (sessionId: string, userId: string) =>
    apiFetch<{ session: SessionSummary }>(`/api/sessions/${encodeURIComponent(sessionId)}/kick`, { method: 'POST', body: JSON.stringify({ userId }) }),
  editSession: (sessionId: string, fields: Record<string, unknown>) =>
    apiFetch<{ session: SessionSummary }>(`/api/sessions/${encodeURIComponent(sessionId)}`, { method: 'PATCH', body: JSON.stringify({ action: 'edit', ...fields }) }),
  blocks: () => apiFetch<{ blocks: BlockedPlayer[] }>('/api/blocks'),
  blockPlayer: (userId: string) => apiFetch('/api/blocks', { method: 'POST', body: JSON.stringify({ userId }) }),
  unblockPlayer: (userId: string) => apiFetch(`/api/blocks/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
  reportPlayer: (subjectUserId: string, reason: string, details: string, sessionId?: string) =>
    apiFetch<{ message: string }>('/api/reports', { method: 'POST', body: JSON.stringify({ subjectUserId, reason, details, sessionId }) }),
  adminReports: () => apiFetch<{ reports: AdminReport[] }>('/api/admin/reports'),
  resolveReport: (id: string, status: 'resolved' | 'dismissed' | 'open') =>
    apiFetch('/api/admin/reports', { method: 'PATCH', body: JSON.stringify({ id, status }) }),

  pushKey: () => apiFetch<{ configured: boolean; publicKey: string | null }>('/api/push/key'),
  savePushSubscription: (subscription: PushSubscriptionJSON) =>
    apiFetch('/api/push/subscribe', { method: 'POST', body: JSON.stringify(subscription) }),
  removePushSubscription: (endpoint: string) =>
    apiFetch('/api/push/subscribe', { method: 'DELETE', body: JSON.stringify({ endpoint }) }),
  sendTestPush: () => apiFetch('/api/push/test', { method: 'POST', body: '{}' }),

  games: () => apiFetch<{ games: Game[] }>('/api/games'),

  profile: () => apiFetch<{ profile: PlayerProfile }>('/api/profile'),
  updateProfile: (patch: Record<string, unknown>) =>
    apiFetch<{ profile: PlayerProfile }>('/api/profile', { method: 'PATCH', body: JSON.stringify(patch) }),

  me: () => apiFetch<MeSummary>('/api/me'),
  notifications: () => apiFetch<{ notifications: AppNotification[] }>('/api/notifications'),
  markNotificationsRead: () => apiFetch('/api/notifications', { method: 'POST', body: '{}' }),

  players: (params: { game?: string; q?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.game) search.set('game', params.game);
    if (params.q) search.set('q', params.q);
    const query = search.toString();
    return apiFetch<{ players: PlayerProfile[] }>(`/api/players${query ? `?${query}` : ''}`);
  },
  player: (idOrUsername: string) =>
    apiFetch<{ player: PlayerProfile; recentCommends: Array<{ fromUsername: string; tags: string[]; createdAt: string }> }>(`/api/players/${encodeURIComponent(idOrUsername)}`),

  sessions: (params: { game?: string; scope?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.game) search.set('game', params.game);
    if (params.scope) search.set('scope', params.scope);
    const query = search.toString();
    return apiFetch<{ sessions: SessionSummary[] }>(`/api/sessions${query ? `?${query}` : ''}`);
  },
  session: (id: string) => apiFetch<SessionDetailPayload>(`/api/sessions/${encodeURIComponent(id)}`),
  createSession: (body: Record<string, unknown>) =>
    apiFetch<{ session: SessionSummary }>('/api/sessions', { method: 'POST', body: JSON.stringify(body) }),
  joinSession: (id: string, role?: string | null) =>
    apiFetch<{ session: SessionSummary }>(`/api/sessions/${encodeURIComponent(id)}/join`, { method: 'POST', body: JSON.stringify({ role: role ?? null }) }),
  leaveSession: (id: string) =>
    apiFetch<{ session: SessionSummary }>(`/api/sessions/${encodeURIComponent(id)}/join`, { method: 'DELETE' }),
  toggleReady: (id: string) =>
    apiFetch<{ session: SessionSummary; ready: boolean }>(`/api/sessions/${encodeURIComponent(id)}/ready`, { method: 'POST', body: '{}' }),
  manageSession: (id: string, action: 'cancel' | 'complete') =>
    apiFetch<{ session: SessionSummary }>(`/api/sessions/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ action }) }),
  submitReview: (sessionId: string, toUserId: string, wouldPlayAgain: boolean | null, tags: string[], comment?: string) =>
    apiFetch<{ pointsEarned: number; awards: string[] }>(`/api/sessions/${encodeURIComponent(sessionId)}/reviews`, {
      method: 'POST',
      body: JSON.stringify({ toUserId, wouldPlayAgain, tags, comment })
    })
};
